import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { FaShoppingCart, FaUserPlus, FaSignInAlt, FaSignOutAlt } from "react-icons/fa";
import { MdDashboard } from "react-icons/md"; 

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.95) 0%, 
    rgba(248, 250, 252, 0.95) 100%);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  z-index: 1000;
  height: 70px;
  transition: all 0.3s ease;
`;

const HeaderContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2563eb;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    color: #1d4ed8;
    transform: translateY(-1px);
  }

  img {
    height: 40px;
    width: auto;
  }
`;

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 18px;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.98) 0%, 
      rgba(248, 250, 252, 0.98) 100%);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(226, 232, 240, 0.6);
    flex-direction: column;
    padding: 1.5rem;
    gap: 1.5rem;
    animation: ${slideDown} 0.3s ease;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    color: #2563eb;
    background-color: rgba(37, 99, 235, 0.1);
    transform: translateY(-2px);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    width: 0;
    height: 2px;
    background-color: #2563eb;
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  &:hover::after {
    width: 80%;
  }
`;


const UserIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  
  &::before {
    content: '👤';
    font-size: 16px;
  }
`;

const LoginIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  
  &::before {
    content: '🔐';
    font-size: 16px;
  }
`;

const DashboardIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  
  &::before {
    content: '📊';
    font-size: 16px;
  }
`;

const LogoutIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  
  &::before {
    content: '🚪';
    font-size: 16px;
  }
`;

const MenuIcon = styled.span`
  display: inline-block;
  width: 24px;
  height: 24px;
  
  &::before {
    content: '☰';
    font-size: 20px;
  }
`;

const CloseIcon = styled.span`
  display: inline-block;
  width: 24px;
  height: 24px;
  
  &::before {
    content: '✕';
    font-size: 18px;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    color: #2563eb;
    background-color: rgba(37, 99, 235, 0.1);
    transform: translateY(-2px);
  }
`;

const CartButton = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: 0.625rem 1.25rem;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);

  &:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulse} 2s infinite;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #374151;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    color: #2563eb;
    background-color: rgba(37, 99, 235, 0.1);
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 1rem;
  }
`;

const PrimaryButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: 0.625rem 1.25rem;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);

  &:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const SecondaryButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
  padding: 0.625rem 1.25rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(37, 99, 235, 0.2);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(37, 99, 235, 0.15);
    border-color: rgba(37, 99, 235, 0.3);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const UserDropdown = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  border: 1px solid rgba(37, 99, 235, 0.2);
  font-weight: 500;
  cursor: pointer;
  padding: 0.625rem 1rem;
  border-radius: 0.75rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(37, 99, 235, 0.15);
    border-color: rgba(37, 99, 235, 0.3);
    transform: translateY(-2px);
  }
`;

const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/shop');
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <HeaderWrapper>
      <HeaderContainer>
        <Logo to="/shop" onClick={closeMobileMenu}>
          <img src="/logo-dark.png" alt="techy garry logo" />
        </Logo>

        <MobileMenuButton onClick={toggleMobileMenu}>
          {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </MobileMenuButton>

        <Nav isOpen={mobileMenuOpen}>
          <NavLink to="/shop" onClick={closeMobileMenu}>
            <span>Shop</span>
          </NavLink>

          <UserSection>
            <NavLink to="/cart" onClick={closeMobileMenu}>
               <FaShoppingCart width={14} title='cart'/>
              <span>Cart</span>
              {itemCount > 0 && <CartBadge>{itemCount}</CartBadge>}
            </NavLink>

            {isAuthenticated ? (
              <UserDropdown>
                <NavLink to="/dashboard" onClick={closeMobileMenu}>
                  <MdDashboard title="Dashboard" width={14} />
                  <span>Dashboard</span>
                </NavLink>
                <UserButton onClick={handleLogout}>
                   <FaSignOutAlt title="Logout" width={14} />
                  <span>Logout</span>
                </UserButton>
              </UserDropdown>
            ) : (
              <AuthButtons>
                <SecondaryButton to="/signin" onClick={closeMobileMenu}>
                 <FaSignInAlt title="Sign In" width={14} /> 
                  <span>Sign In</span>
                </SecondaryButton>
                <PrimaryButton to="/register" onClick={closeMobileMenu}>
                  <FaUserPlus title="Register" width={14} />
                  <span>Register</span>
                </PrimaryButton>
              </AuthButtons>
            )}
          </UserSection>
        </Nav>
      </HeaderContainer>
    </HeaderWrapper>
  );
};

export default Header;